import { urlsApi } from './api';
import { AxiosResponse } from 'axios';
import { JsExtractorPreview } from '../../../models/preview';

/**
 * Response interface for URL testing
 */
export interface TestUrlResponse {
  message: string;
  propertyData: any;
  error?: string;
  html?: string;
  jsExtractorResults?: JsExtractorPreview[];
}

/**
 * Generic success message response
 */
export interface SuccessResponse {
  message: string;
}

/**
 * Test a URL to see if it can be parsed without saving to the database
 * @param sourceId The source ID
 * @param sourceUrl The URL to test
 * @returns The parsed property data and source information
 */
export const testUrl = async (sourceId: string, sourceUrl: string): Promise<TestUrlResponse> => {
  try {
    // The API expects an object with a url property
    const response: AxiosResponse<TestUrlResponse> = await urlsApi.test(sourceId, sourceUrl);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return {
        message: 'URL could not be parsed',
        propertyData: null,
        error: 'Failed to parse URL with the source configuration'
      };
    }
    
    throw error;
  }
};

/**
 * Parse a URL to extract property data without saving
 * @param sourceId The source ID
 * @param sourceUrl The URL to parse
 * @returns The parsed property data
 */
export const parseUrl = async (sourceId: string, sourceUrl: string): Promise<any> => {
  const response: AxiosResponse<any> = await urlsApi.parse(sourceId, sourceUrl);
  return response.data;
};

/**
 * Process a URL and save the extracted property to the database
 * @param sourceId The source ID
 * @param urlHash The URL hash
 * @returns Success message
 */
export const processUrl = async (sourceId: string, urlHash: string): Promise<SuccessResponse> => {
  const response: AxiosResponse<SuccessResponse> = await urlsApi.process(sourceId, urlHash);
  return response.data;
};

/**
 * Delete a URL from the database
 * @param sourceId The source ID
 * @param urlHash The URL hash
 * @returns Success message
 */
export const deleteUrl = async (sourceId: string, urlHash: string): Promise<SuccessResponse> => {
  const response: AxiosResponse<SuccessResponse> = await urlsApi.delete(sourceId, urlHash);
  return response.data;
};

/**
 * Update a URL's active status
 * @param sourceId The source ID
 * @param urlHash The URL hash
 * @param active Whether the URL should be active
 * @returns Success message
 */
export const updateUrlActive = async (
  sourceId: string,
  urlHash: string,
  active: boolean
): Promise<SuccessResponse> => {
  const response: AxiosResponse<SuccessResponse> = await urlsApi.updateUrl(sourceId, urlHash, { active });
  return response.data;
};

export const updateUrlFullScrapeRequired = async (
  sourceId: string,
  urlHash: string,
  fullScrapeRequired: boolean
): Promise<SuccessResponse> => {
  const response: AxiosResponse<SuccessResponse> = await urlsApi.updateUrl(
    sourceId,
    urlHash,
    { fullScrapeRequired }
  );
  return response.data;
};
